import { fetchAllPages } from '@/lib/fetch-all-pages';
import { graphqlClient } from '@/lib/graphql-client';
import { OrderBy } from '@/lib/graphql-query-builder';
import {
  dateOnlyOf,
  dateOnlyRangeClause,
  orgCalendarDate,
} from '@/lib/utils/custom-date/custom-date';
import { DatedHrTerm } from '@/lib/utils/work-days/work-days';
import { postHrmApi, type HrmApiAction } from '@/lib/hrm-api';
import { findWorkflowOutput, readWorkflowResponse, WorkflowResponse } from '@/lib/workflow-webhook';
import {
  AttendancePolicy,
  AttendanceRuleError,
  attendanceDateClause,
  attendanceDayOf,
  getOrgDateString,
  getOrgDayRange,
  isAttendanceInProgress,
  TermWorkDay,
} from './attendance-rules';
import {
  AttendanceDayHoliday,
  AttendanceDayLeave,
  AttendanceDayRow,
  buildAttendanceDays,
} from './attendance-days';
import {
  buildGetAttendanceCorrectionsQuery,
  buildGetAttendancesQuery,
  buildGetDayListHolidaysQuery,
  buildGetDayListLeavesQuery,
  buildGetMyFinesQuery,
  buildGetMyHrPeriodQuery,
  buildGetMyHrTermsQuery,
} from '../graphql/queries';
import {
  AttendanceCorrectionInsertInput,
  AttendanceCorrectionsPagingResult,
  AttendanceRecord,
  AttendancesPagingResult,
  BreakTimeDetail,
  BreakType,
  FineSummary,
  GetAttendanceCorrectionsParams,
  GetAttendancesParams,
  TodayAttendance,
} from '../types/attendance.types';

/**
 * Reads go to the Data Gateway; every write goes through one route of the hrm-attendance Blocks Function
 * (src/lib/hrm-api.ts), which re-reads the stored day, applies the attendance rules on the server clock and
 * writes the record (source: blocks/workflows/attendance/, routes: blocks/functions/hrm-attendance/):
 * - ATTENDANCE_CLOCK_IN / _CLOCK_OUT / _BREAK_START / _BREAK_END
 * - ATTENDANCE_CORRECTION_REQUEST: submit a correction, then mail + notify the department's heads
 * The rules in attendance-rules.ts still drive what the widget offers; the function is the authority.
 */
const CLOCK_ACTIONS = {
  clockIn: {
    action: 'ATTENDANCE_CLOCK_IN',
    failureCode: 'ATTENDANCE_CLOCK_IN_FAILED',
  },
  clockOut: {
    action: 'ATTENDANCE_CLOCK_OUT',
    failureCode: 'ATTENDANCE_CLOCK_OUT_FAILED',
  },
  startBreak: {
    action: 'ATTENDANCE_BREAK_START',
    failureCode: 'ATTENDANCE_BREAK_START_FAILED',
  },
  endBreak: {
    action: 'ATTENDANCE_BREAK_END',
    failureCode: 'ATTENDANCE_BREAK_END_FAILED',
  },
} as const satisfies Record<string, { action: HrmApiAction; failureCode: string }>;

/**
 * `BreakTimeDetails` on Attendance / AttendanceCorrection is an embedded `AttendanceBreak[]`.
 * All break times are UTC instants. A read gives `null` for an empty list and for unset
 * sub-fields; records written before the child entity held a JSON string instead, still read here.
 */
const parseBreakTimeDetails = (raw: unknown): BreakTimeDetail[] => {
  let entries: unknown = raw;
  if (typeof raw === 'string') {
    try {
      entries = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(entries)) return [];
  return entries
    .filter((entry): entry is Record<string, string | null> => Boolean(entry?.StartTimeUtc))
    .map(({ StartTimeUtc, EndTimeUtc, Type }) => ({
      StartTimeUtc: StartTimeUtc as string,
      ...(EndTimeUtc ? { EndTimeUtc } : {}),
      ...(Type ? { Type: Type as BreakType } : {}),
    }));
};

export const getAttendances = async (
  params: GetAttendancesParams
): Promise<{ Attendances: AttendancesPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  const result = (await graphqlClient.query({
    query: buildGetAttendancesQuery(filter, pageNo, pageSize, order),
  })) as { Attendances: AttendancesPagingResult };
  return {
    Attendances: {
      ...result.Attendances,
      // Sorted by the server (newest day first unless `order` says otherwise).
      items: result.Attendances.items.map((item) => ({
        ...item,
        BreakTimeDetails: parseBreakTimeDetails(item.BreakTimeDetails),
      })),
    },
  };
};

/**
 * The employee's Attendance record for the organization day `day` falls on, by that day's
 * `AttendanceDate` (its UTC midnight, see `attendanceDateClause`). The unique
 * (OrganizationId, UserId, AttendanceDate) index keeps it to one record.
 */
const getAttendanceOnDay = async (userId: string, day: Date): Promise<TodayAttendance | null> => {
  const filter = {
    UserId: userId,
    AttendanceDate: attendanceDateClause(getOrgDateString(day)),
  };
  const result = (await graphqlClient.query({
    query: buildGetAttendancesQuery(filter, 1, 5),
  })) as { Attendances: AttendancesPagingResult };

  const item = result.Attendances.items[0];
  return item ? { ...item, BreakTimeDetails: parseBreakTimeDetails(item.BreakTimeDetails) } : null;
};

/** The employee's current working day: today's record, or yesterday's still in progress (a night
 * shift past midnight is clocked out on the day after its clock-in). */
export const getTodayAttendance = async (userId: string): Promise<TodayAttendance | null> => {
  const now = new Date();
  const today = await getAttendanceOnDay(userId, now);
  if (today) return today;
  const yesterday = await getAttendanceOnDay(
    userId,
    new Date(getOrgDayRange(now).start.getTime() - 1)
  );
  return yesterday && isAttendanceInProgress(yesterday, now) ? yesterday : null;
};

/** `value` (a UTC instant, or a plain "YYYY-MM-DD") falls on a day from the HR term's first to its
 * last day, both inclusive. The bounds are date-only fields, so this compares organization days, not
 * instants. Missing bounds (no HR period resolved yet) mean "don't filter". */
const isWithinHrPeriod = (value: string | undefined, start?: string, end?: string): boolean => {
  // A record without a date only falls outside a period that has a start.
  if (!value) return !start;
  const day = orgCalendarDate(value);
  if (start && day < dateOnlyOf(start)) return false;
  if (end && day > dateOnlyOf(end)) return false;
  return true;
};

/** An HR term with its work days, as `getMyHrTerms` reads it. */
export type HrTermWorkDays = DatedHrTerm & { WorkDays?: TermWorkDay[] | null };

/** The employee's HR terms with their work days (match one to a day with `hrTermOn`). */
export const getMyHrTerms = async (userId: string): Promise<HrTermWorkDays[]> => {
  const result = (await graphqlClient.query({
    query: buildGetMyHrTermsQuery({ UserId: userId }),
  })) as { HrProfiles: { items: HrTermWorkDays[] } };
  return result.HrProfiles.items;
};

// Generous upper bound for a range's leave requests and holidays.
const DAY_LIST_PAGE_SIZE = 500;

/**
 * Every day from `from` to `to` ("YYYY-MM-DD", inclusive) of one employee, newest first: their records
 * with the approved and pending leave, national holidays and HR term work days that say what a day without one is
 * (`buildAttendanceDays`). Every record of the range is read, so no day is shown absent by mistake.
 */
export const getAttendanceDays = async ({
  userId,
  from,
  to,
}: {
  userId: string;
  from: string;
  to: string;
}): Promise<AttendanceDayRow<AttendanceRecord>[]> => {
  const range = dateOnlyRangeClause(from, to);
  if (!userId || !range?.$gte || !range.$lt) return [];
  const [records, leaveResult, holidayResult, terms] = await Promise.all([
    fetchAllPages(async (pageNo, pageSize) => {
      const page = await getAttendances({
        pageNo,
        pageSize,
        filter: { UserId: userId, AttendanceDate: range },
      });
      return page.Attendances;
    }),
    graphqlClient.query<{ LeaveRequests: { items: AttendanceDayLeave[] } }>({
      query: buildGetDayListLeavesQuery(
        {
          UserId: userId,
          Status: { $in: ['Approved', 'Pending'] },
          StartDate: { $lt: range.$lt },
          EndDate: { $gte: range.$gte },
        },
        DAY_LIST_PAGE_SIZE
      ),
    }),
    // National holidays only, like the attendance workflows' expected hours.
    graphqlClient.query<{ Holidays: { items: AttendanceDayHoliday[] } }>({
      query: buildGetDayListHolidaysQuery(
        { IsRegional: { $ne: true }, StartDate: range },
        DAY_LIST_PAGE_SIZE
      ),
    }),
    getMyHrTerms(userId),
  ]);
  return buildAttendanceDays({
    from,
    to,
    today: getOrgDateString(new Date()),
    records,
    leaves: leaveResult.LeaveRequests.items,
    holidays: holidayResult.Holidays.items,
    terms,
  });
};

/**
 * Current-HR-year fine breakdown for the clock widget, ported from the legacy .NET
 * `FineServiceHelper.GetFinesAsync`: lunch/non-compliance totals sum `Fine` records by
 * `FineType`, the attendance total sums `AttendanceCorrection.Fine` of approved corrections only
 * (a correction is fined on approval; a pending one only carries the amount its approval would
 * charge), and all three are scoped to the employee's current (`IsCurrentHrYear`) HR period.
 * Every page of fines and approved corrections is read, so a long year is never cut off.
 *
 * All three now key on the same `UserId` (the IAM user id) — see the known-gap notes on
 * `buildGetMyHrPeriodQuery`/`buildGetMyFinesQuery` for why the HR-period and Fine lookups may
 * still return nothing until their write paths are updated to populate `UserId` too.
 */
export const getMyFineSummary = async (userId: string): Promise<FineSummary> => {
  const periodFilter = { UserId: userId, IsCurrentHrYear: true };
  const finesFilter = { UserId: userId };
  const correctionsFilter = { UserId: userId, Status: 'Approved' };

  type FineRow = { FineType?: string; FineAmount?: number; FineCreatedDateUtc?: string };
  // Paged in a stable order (ties broken by ItemId), so no row repeats or goes missing between pages.
  const finesOrder: OrderBy[] = [
    { field: 'FineCreatedDateUtc', direction: 'ASC' },
    { field: 'ItemId', direction: 'ASC' },
  ];
  const correctionsOrder: OrderBy[] = [
    { field: 'CorrectionDate', direction: 'ASC' },
    { field: 'ItemId', direction: 'ASC' },
  ];
  const [periodResult, fines, corrections] = await Promise.all([
    graphqlClient.query({
      query: buildGetMyHrPeriodQuery(periodFilter, 1, 1),
    }) as Promise<{
      HrProfiles: { items: { HRPeriodStartDate?: string; HRPeriodEndDate?: string }[] };
    }>,
    fetchAllPages(async (pageNo, pageSize) => {
      const page = (await graphqlClient.query({
        query: buildGetMyFinesQuery(finesFilter, pageNo, pageSize, finesOrder),
      })) as { Fines: { items: FineRow[]; totalCount: number } };
      return page.Fines;
    }),
    fetchAllPages(async (pageNo, pageSize) => {
      const page = await getAttendanceCorrections({
        pageNo,
        pageSize,
        filter: correctionsFilter,
        order: correctionsOrder,
      });
      return page.AttendanceCorrections;
    }),
  ]);

  const period = periodResult.HrProfiles.items[0];

  let lunch = 0;
  let nonCompliance = 0;
  fines.forEach((fine) => {
    if (
      !isWithinHrPeriod(fine.FineCreatedDateUtc, period?.HRPeriodStartDate, period?.HRPeriodEndDate)
    )
      return;
    if (fine.FineType === 'LunchFine') lunch += fine.FineAmount ?? 0;
    if (fine.FineType === 'NonComplianceFine') nonCompliance += fine.FineAmount ?? 0;
  });

  const attendance = corrections.reduce((sum, correction) => {
    if (correction.Status !== 'Approved') return sum;
    if (
      !isWithinHrPeriod(
        correction.CorrectionDate && attendanceDayOf(correction.CorrectionDate),
        period?.HRPeriodStartDate,
        period?.HRPeriodEndDate
      )
    ) {
      return sum;
    }
    return sum + (correction.Fine ?? 0);
  }, 0);

  return { attendance, lunch, nonCompliance, grandTotal: attendance + lunch + nonCompliance };
};

/** The refusal a unique-index rejection means, per workflow (see `callAttendanceWorkflow`). */
const DUPLICATE_REFUSALS: Record<string, string> = {
  ATTENDANCE_CLOCK_IN_FAILED: 'ATTENDANCE_ALREADY_CLOCKED_IN_TODAY',
  ATTENDANCE_CORRECTION_REQUEST_FAILED: 'ATTENDANCE_CORRECTION_REQUEST_ALREADY_EXISTS',
};

/** What a refused attendance workflow names: the translation key and its placeholders. */
interface WorkflowRefusal {
  MessageKey?: string;
  MessageParams?: Record<string, string | number>;
}

/**
 * Calls an attendance action of the hrm-attendance function and returns its answer. A refusal (`IsSuccessful: false`) is
 * thrown as an `AttendanceRuleError` keyed by the workflow's `Data.MessageKey` (input errors have
 * none and use `Message`), so `useTranslateRuleErrors` shows it translated; `Message` is the
 * English fallback for a key the app does not know.
 */
export const callAttendanceWorkflow = async <TData>(
  action: HrmApiAction,
  body: Record<string, unknown>,
  failureCode: string
): Promise<WorkflowResponse<TData>> =>
  readAttendanceAnswer<TData>(await postHrmApi(action, body), failureCode);

/** An attendance answer (an hrm-attendance route, or the ATTENDANCE_IMPORT workflow): the envelope, a
 * refusal thrown as an `AttendanceRuleError`. */
export const readAttendanceAnswer = <TData>(
  response: unknown,
  failureCode: string
): WorkflowResponse<TData> => {
  // A write a unique index refuses ends the run without the envelope: a second clock-in for the day
  // (Attendance) or a second pending correction for the day (AttendanceCorrection.PendingKey) that
  // raced past the workflow's own check.
  const duplicateKey = DUPLICATE_REFUSALS[failureCode];
  if (
    duplicateKey &&
    !findWorkflowOutput(response, 'IsSuccessful') &&
    /already exists/i.test(JSON.stringify(response ?? ''))
  ) {
    throw new AttendanceRuleError(duplicateKey);
  }
  const result = readWorkflowResponse<TData & WorkflowRefusal>(response, failureCode);
  if (!result.IsSuccessful) {
    const refusal = (result.Data ?? {}) as WorkflowRefusal;
    throw new AttendanceRuleError(
      refusal.MessageKey || result.Message || failureCode,
      refusal.MessageParams,
      result.Message
    );
  }
  return result;
};

/** Who is clocking and which rules bind them — see `AttendancePolicy` in attendance-rules.ts. */
export interface AttendanceRuleContext {
  policy: AttendancePolicy;
  isLoading?: boolean;
  isWFH?: boolean;
  wfoDays?: string[];
}

type ClockAction = keyof typeof CLOCK_ACTIONS;

/**
 * One clock action through its own workflow. Who clocks, when, and from where come from the session
 * and the server; the body carries only the action's own input (never an IP address: a browser's
 * report of its own address proves nothing).
 */
const runClockAction = (action: ClockAction, body: Record<string, unknown> = {}) => {
  const { action: route, failureCode } = CLOCK_ACTIONS[action];
  return callAttendanceWorkflow<{ Action: ClockAction }>(route, body, failureCode);
};

export const clockIn = () => runClockAction('clockIn');

/** Opens a break of `type`; the open break is stored at once, so it survives a page refresh. */
export const startBreak = ({ type }: { type: BreakType }) =>
  runClockAction('startBreak', { breakType: type });

export const endBreak = () => runClockAction('endBreak');

/**
 * Ends the day (or the current session). `enteredBreaks` are breaks typed in at clock-out by an
 * employee who logged none live. Totals, expected hours, overtime and the day status are computed by
 * the workflow.
 */
export const clockOut = ({
  enteredBreaks,
  captchaToken,
}: { enteredBreaks?: BreakTimeDetail[]; captchaToken?: string } = {}) =>
  runClockAction('clockOut', {
    ...(enteredBreaks && enteredBreaks.length > 0
      ? {
          enteredBreaks: enteredBreaks.map(({ StartTimeUtc, EndTimeUtc, Type }) => ({
            StartTimeUtc,
            EndTimeUtc,
            ...(Type ? { Type } : {}),
          })),
        }
      : {}),
    // CAPTCHA_ON_CLOCK_OUT: verified by the workflow with Google, never trusted here.
    ...(captchaToken ? { captchaToken } : {}),
  });

export const getAttendanceCorrections = async (
  params: GetAttendanceCorrectionsParams
): Promise<{ AttendanceCorrections: AttendanceCorrectionsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  const result = (await graphqlClient.query({
    query: buildGetAttendanceCorrectionsQuery(filter, pageNo, pageSize, order),
  })) as { AttendanceCorrections: AttendanceCorrectionsPagingResult };
  return {
    AttendanceCorrections: {
      ...result.AttendanceCorrections,
      // Sorted by the server (latest corrected day first unless `order` says otherwise).
      items: result.AttendanceCorrections.items.map((item) => ({
        ...item,
        BreakTimeDetails: parseBreakTimeDetails(item.BreakTimeDetails),
      })),
    },
  };
};

/**
 * Submits a correction through ATTENDANCE_CORRECTION_REQUEST. The workflow takes the employee,
 * their department and the fine from the session and their permissions, checks the rules (one
 * pending request per day, a day still being worked, times on the day, breaks, totals), saves the
 * request as Pending and mails + notifies the department's heads.
 */
export const submitAttendanceCorrection = (
  input: Pick<
    AttendanceCorrectionInsertInput,
    'CorrectionDate' | 'StartTimeUtc' | 'EndTimeUtc' | 'BreakTimeDetails' | 'Comment'
  > & { DayType?: 'fullday' | 'halfday' }
) =>
  callAttendanceWorkflow<{
    Status: string;
    HobCount: number;
    Notified: boolean;
    /** `CORRECTION_REQUEST_SUBMITTED`, or `..._NO_HOB` when no head of business was found to notify. */
    MessageKey: string;
  }>(
    'ATTENDANCE_CORRECTION_REQUEST',
    {
      correctionDate: input.CorrectionDate,
      startTimeUtc: input.StartTimeUtc,
      endTimeUtc: input.EndTimeUtc,
      breakTimeDetails: (input.BreakTimeDetails ?? []).map(
        ({ StartTimeUtc, EndTimeUtc, Type }) => ({
          StartTimeUtc,
          EndTimeUtc,
          Type: Type === 'Lunch' ? 'Lunch' : 'Regular',
        })
      ),
      ...(input.Comment ? { comment: input.Comment } : {}),
      ...(input.DayType ? { dayType: input.DayType } : {}),
    },
    'ATTENDANCE_CORRECTION_REQUEST_FAILED'
  );
